import type { ReactNode } from 'react'
import { isDemoStandEnabled } from './dev/demoStand'
import { DemoIikoScreen } from './dev/DemoIikoScreen'

import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { AuthProvider, useAuth } from './auth/AuthProvider'
import { homePathForUser } from './auth/routing'
import { CartProvider } from './cart/CartProvider'
import { ActiveVenueProvider } from './venues/ActiveVenueProvider'
import { AppShell } from './components/AppShell'
import { FirstOrganizationWorkspace } from './components/FirstOrganizationWorkspace'
import { OperatorShell } from './components/OperatorShell'
import { ToastProvider } from './components/ToastProvider'
import { isInventoryFeatureEnabled } from './inventory/inventoryFeature'
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen'
import { ResetPasswordScreen } from './screens/ResetPasswordScreen'
import { LoginScreen, RequireAuth } from './screens/LoginScreen'
import { RegisterScreen } from './screens/RegisterScreen'
import { PersonalDataConsentLegalScreen } from './screens/PersonalDataConsentLegalScreen'
import { NotificationsConsentLegalScreen } from './screens/NotificationsConsentLegalScreen'
import { PublicOfferLegalScreen } from './screens/PublicOfferLegalScreen'
import { InventoryIntegrationIntroScreen } from './screens/InventoryIntegrationIntroScreen'
import { IikoIntegrationGuideScreen } from './screens/IikoIntegrationGuideScreen'
import { RestaurantPurchaseScreen } from './screens/RestaurantPurchaseScreen'
import { OperatorCatalogReviewScreen } from './screens/OperatorCatalogReviewScreen'
import {
  OperatorRestaurantOrganizationScreen,
  OperatorSupplierOrganizationScreen,
} from './screens/OperatorOrganizationScreen'
import { OperatorRestaurantPricesScreen } from './screens/OperatorRestaurantPricesScreen'
import { OperatorRestaurantRfqsScreen } from './screens/OperatorRestaurantRfqsScreen'
import { OperatorRestaurantsScreen } from './screens/OperatorRestaurantsScreen'
import { OperatorRfqScreen } from './screens/OperatorRfqScreen'
import { OperatorSuppliersScreen } from './screens/OperatorSuppliersScreen'
import { RequestScreen } from './screens/RequestScreen'
import { RequireMarketUser } from './screens/RequireMarketUser'
import { RequireOperator } from './screens/RequireOperator'
import { SettingsScreen } from './screens/SettingsScreen'
import { SupplierCatalogImportScreen } from './screens/SupplierCatalogImportScreen'
import { RestaurantSuppliersScreen } from './screens/RestaurantSuppliersScreen'
import { RestaurantMyPricesScreen } from './screens/RestaurantMyPricesScreen'
import { MarketAnalyticsScreen } from './screens/MarketAnalyticsScreen'

const HomeRedirect = () => {
  const { userRoles } = useAuth()
  return <Navigate to={homePathForUser(userRoles)} replace />
}

/**
 * A quick-signup account has no tenant yet. Keep the chosen address intact so
 * the first-organization prompt describes the feature the user tried to open.
 */
const RequireOrganization = () => {
  const { session } = useAuth()
  return session?.organizationId == null ? <FirstOrganizationWorkspace /> : <Outlet />
}

/**
 * Маркетплейс и кабинет поставщика убраны. Старые адреса остаются перенаправлениями: на них
 * ведут закладки и ссылки из прежних писем. Поставщику — в настройки, ресторану — куда сказано.
 */
const LegacyRedirect = ({ buyerTo }: { buyerTo: string }) => {
  const { role } = useAuth()
  return <Navigate to={role === 'supplier' ? '/settings' : buyerTo} replace />
}

const PaymentsRoute = () => {
  const { search } = useLocation()
  return <Navigate to={`/settings/billing${search}`} replace />
}

const PurchaseRoute = () =>
  isInventoryFeatureEnabled() ? <RestaurantPurchaseScreen /> : <InventoryIntegrationIntroScreen />

function HideOnDemoStand({ children }: { children: ReactNode }) {
  if (isDemoStandEnabled()) return <Navigate to="/my-prices" replace />
  return children
}

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginScreen />} />
    <Route path="/register" element={<RegisterScreen />} />
    <Route path="/legal/personal-data-consent" element={<PersonalDataConsentLegalScreen />} />
    <Route path="/legal/notifications-consent" element={<NotificationsConsentLegalScreen />} />
    <Route path="/legal/offer" element={<PublicOfferLegalScreen />} />
    <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
    <Route path="/reset-password" element={<ResetPasswordScreen />} />
    <Route
      element={
        <RequireAuth>
          <RequireMarketUser>
            <AppShell />
          </RequireMarketUser>
        </RequireAuth>
      }
    >
      <Route index element={<HomeRedirect />} />
      <Route element={<RequireOrganization />}>
        <Route path="billing/checkout" element={<Navigate to="/settings/billing" replace />} />
        <Route path="overview" element={<LegacyRedirect buyerTo="/my-prices" />} />
        <Route path="catalog" element={<LegacyRedirect buyerTo="/my-prices" />} />
        <Route path="catalog/import" element={<LegacyRedirect buyerTo="/my-prices/import" />} />
        <Route path="my-prices" element={<RestaurantMyPricesScreen />} />
        <Route path="my-prices/suppliers" element={<RestaurantSuppliersScreen />} />
        <Route path="my-prices/import" element={<SupplierCatalogImportScreen />} />
        <Route path="cart" element={<RequestScreen />} />
        <Route path="orders" element={<LegacyRedirect buyerTo="/cart?tab=history" />} />
        <Route path="orders/create" element={<LegacyRedirect buyerTo="/my-prices" />} />
        <Route path="suppliers" element={<LegacyRedirect buyerTo="/my-prices" />} />
        <Route path="customers" element={<LegacyRedirect buyerTo="/my-prices" />} />
        <Route path="notifications" element={<LegacyRedirect buyerTo="/my-prices" />} />
        <Route path="payments" element={<PaymentsRoute />} />
        <Route path="analytics" element={<MarketAnalyticsScreen />} />
        <Route path="purchase" element={<PurchaseRoute />} />
        <Route
          path="demo/iiko"
          element={isDemoStandEnabled() ? <DemoIikoScreen /> : <Navigate to="/my-prices" replace />}
        />
        <Route path="settings" element={<Navigate to="/settings/organization" replace />} />
        <Route
          path="settings/:tab"
          element={
            <HideOnDemoStand>
              <SettingsScreen />
            </HideOnDemoStand>
          }
        />
        <Route path="chat" element={<LegacyRedirect buyerTo="/my-prices" />} />
      </Route>
      <Route path="help/iiko" element={<IikoIntegrationGuideScreen />} />
    </Route>
    <Route
      path="/operator"
      element={
        <RequireOperator>
          <OperatorShell />
        </RequireOperator>
      }
    >
      <Route index element={<OperatorSuppliersScreen />} />
      <Route path="suppliers/:organizationId" element={<OperatorSupplierOrganizationScreen />} />
      <Route path="suppliers/:organizationId/catalog" element={<OperatorCatalogReviewScreen />} />
      <Route
        path="suppliers/:organizationId/catalog/import"
        element={<SupplierCatalogImportScreen />}
      />
      <Route path="buyers" element={<OperatorRestaurantsScreen />} />
      <Route path="buyers/:organizationId" element={<OperatorRestaurantOrganizationScreen />} />
      <Route path="buyers/:organizationId/prices" element={<OperatorRestaurantPricesScreen />} />
      <Route
        path="buyers/:organizationId/prices/import"
        element={<SupplierCatalogImportScreen />}
      />
      <Route path="buyers/:organizationId/rfqs" element={<OperatorRestaurantRfqsScreen />} />
      <Route path="rfqs/:rfqId" element={<OperatorRfqScreen />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <ActiveVenueProvider>
        <CartProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </CartProvider>
      </ActiveVenueProvider>
    </BrowserRouter>
  </AuthProvider>
)

export default App
